import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronUp, Rotate3d } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { TableCell, TableRow } from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { FailureMode } from '../../../types';
import { translateWithFallback } from '../../../utils/translation';
import KnowledgeReferenceCard from './KnowledgeReferenceCard';
import { LONG_TEXT_CELL, SCORE_CELL, getSeverityVariant } from './drbfmTableFormat';

interface FailureModeRowProps {
  row: FailureMode;
  drbfmSessionId?: string;
  selected?: boolean;
  onSelect?: (failureMode: FailureMode) => void;
}

const FailureModeRow: React.FC<FailureModeRowProps> = ({
  row,
  drbfmSessionId,
  selected = false,
  onSelect,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const linkedCadModelId = row.cadAnchor?.cadModelId;
  const canView3D = Boolean(linkedCadModelId && drbfmSessionId);

  const handleSelect = () => {
    onSelect?.(row);
  };

  const handleToggleOpen = (event: React.MouseEvent) => {
    event.stopPropagation();
    setOpen(!open);
    handleSelect();
  };

  const handleView3D = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!canView3D) return;
    const params = new URLSearchParams();
    params.set('cad_model', linkedCadModelId!);
    params.set('drbfm_session', drbfmSessionId!);
    // `failure_mode` lets the viewer open the detail drawer directly for the
    // clicked row instead of just showing every pin (TG #673).
    params.set('failure_mode', row.id);
    navigate(`/cad-viewer?${params.toString()}`);
  };

  return (
    <>
      <TableRow
        data-state={selected ? 'selected' : undefined}
        onClick={handleSelect}
        className={cn(onSelect ? 'cursor-pointer' : 'cursor-default')}
      >
        <TableCell>
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={handleToggleOpen}
            aria-label={
              open
                ? t('drbfm.cadLink.failureModeCollapse', '故障モード詳細を閉じる')
                : t('drbfm.cadLink.failureModeExpand', '故障モード詳細を開く')
            }
          >
            {open ? <ChevronUp strokeWidth={1.9} /> : <ChevronDown strokeWidth={1.9} />}
          </Button>
        </TableCell>
        <TableCell>
          <button
            type="button"
            onClick={handleSelect}
            title={row.failureDescription || ''}
            aria-label={translateWithFallback(t, 'drbfm.cadLink.selectFailureModeLabel', `故障モードを選択: ${row.failureDescription}`, {
              description: row.failureDescription,
            })}
            className="block w-full rounded-sm p-0.5 text-left focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
          >
            <span className={cn('block', LONG_TEXT_CELL)}>{row.failureDescription}</span>
          </button>
        </TableCell>
        <TableCell>
          <p className={LONG_TEXT_CELL} title={row.failureCause || ''}>
            {row.failureCause}
          </p>
        </TableCell>
        <TableCell>
          <p className={LONG_TEXT_CELL} title={row.failureEffect || ''}>
            {row.failureEffect}
          </p>
        </TableCell>
        <TableCell>
          <Badge variant={getSeverityVariant(row.severity)} className="revy-cjk">
            {row.severity}
          </Badge>
        </TableCell>
        <TableCell className="text-center">
          <span
            className={cn(
              SCORE_CELL,
              (row.severityScore || 0) >= 8 ? 'text-destructive' : 'text-foreground',
            )}
          >
            {row.severityScore || '-'}
          </span>
        </TableCell>
        <TableCell className="text-center">
          <span
            className={cn(
              SCORE_CELL,
              (row.rpnScore || 0) >= 100 ? 'text-destructive' : 'text-foreground',
            )}
          >
            {row.rpnScore || '-'}
          </span>
        </TableCell>
        <TableCell className="text-center">
          <TooltipProvider delayDuration={400}>
            <Tooltip>
              {/* The trigger wraps the button so the tooltip still opens while
                  the action is disabled, exactly as the MUI span did. */}
              <TooltipTrigger asChild>
                <span className="inline-flex">
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    disabled={!canView3D}
                    onClick={handleView3D}
                    aria-label={t('drbfm.cadLink.view3DLabel', '3Dで見る')}
                  >
                    <Rotate3d strokeWidth={1.9} />
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent className="revy-ui revy-cjk max-w-[16rem]">
                {canView3D
                  ? t('drbfm.cadLink.view3DTooltip', '3Dビューアで該当部位を表示')
                  : t('drbfm.cadLink.view3DUnavailable', 'CADモデルが紐付いていない、またはセッションIDが未指定')}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </TableCell>
      </TableRow>
      {open && (
        <TableRow className="hover:bg-transparent">
          <TableCell className="px-2 py-0" colSpan={8}>
            <div className="m-4">
              {row.knowledgeReferences && row.knowledgeReferences.length > 0 && (
                <>
                  <p className="revy-cjk mb-2 text-sm font-medium leading-[1.5]">
                    {t('drbfm.knowledge.references')}
                  </p>
                  {row.knowledgeReferences.map((reference, idx) => (
                    <KnowledgeReferenceCard key={idx} reference={reference} />
                  ))}
                </>
              )}
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

export default FailureModeRow;
